import loadingBg from '../../asset/images/loadingPage/Demo_fgBg.jpg'
import loadingLogo from '../../asset/images/loadingPage/image_logo.png'
import enabledDot from '../../asset/images/loadingPage/icon_pageB.png'
import disabledDot from '../../asset/images/loadingPage/icon_pageA.png'

import { Container } from '@pixi/react'
import { Assets, BitmapText, TextStyle } from 'pixi.js'
import { FixedContainerV2, Ga4EventType, ga4Info, Image, isXinStarDemoMode, isXinStarPlayMode, t } from '@slot-nx/core'
import { gameConfig } from '../config'
import { useGameStore } from '../domain/game/store/GameStore'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Spine } from '@esotericsoftware/spine-pixi'
import { Container as PixiContainer, Text as PixiText } from 'pixi.js'
import { sleep } from '../domain/game/util/gameUtil'
import gsap from 'gsap'
import { freeGameService } from '../domain/freeGame/service/freeGameService'
import { getloadingPageSpineData } from '../domain/assetManager/resource/spineResource'

Assets.addBundle('Loading', {
  loadingBg: { src: loadingBg, loadParser: 'loadTextures' },
  loadingLogo: { src: loadingLogo, loadParser: 'loadTextures' },
  enabledDot: { src: enabledDot, loadParser: 'loadTextures' },
  disabledDot: { src: disabledDot, loadParser: 'loadTextures' },
  // godziLogo: { src: godziLogo, loadParser: 'loadTextures' },
})

const textStyle = new PixiText()
textStyle.style = new TextStyle({
  fontSize: 30,
  fill: 'ffffff',
  align: 'center',
  fontWeight: '700',
  wordWrap: true,
  wordWrapWidth: 780,
})

const textStyle2 = new TextStyle({
  fontSize: 40,
  fill: '#FFC0D8',
  align: 'center',
})

export const Loading = () => {
  const tutorialRef = useRef<PixiContainer>()
  const textRef = useRef<PixiContainer>()
  const loadingContainerRef = useRef<PixiContainer>()
  const loadingBarRef = useRef<PixiContainer>()
  const percentageRef = useRef<PixiContainer>()
  let loadingSpine: Spine
  let startLoadingTime: number

  const [tutorialAnimationIndex, setTutorialAnimationIndex] = useState(0)
  const [isLoadingDone, setIsLoadingDone] = useState(false)
  const enableTutorialLogo = !isXinStarDemoMode() && !isXinStarPlayMode()

  const cursorSetting = useMemo(() => {
    if (isLoadingDone) {
      return 'pointer'
    }
    else {
      return 'default'
    }
  }, [isLoadingDone])

  useEffect(() => {
    prepareSpine()
    updateLoadingBar()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const prepareSpine = async () => {
    await Assets.load(getloadingPageSpineData())

    if (!tutorialRef.current) return
    startLoadingTime = new Date().getTime()

    if (tutorialRef.current.children.length === 0) {
      loadingSpine = Spine.from('loadingPageSkel', 'loadingPageAtlas', { autoUpdate: true })
      tutorialRef.current.addChild(loadingSpine)
    }
    else {
      loadingSpine = tutorialRef.current.children[0] as Spine
    }
    if (textRef.current) {
      textRef.current.addChild(textStyle)
    }
    loopTutorial()
  }

  const loopTutorial = async () => {
    let firstTime = true
    const changeSec = 7000
    const textArray = [t('tutorial_01'), t('tutorial_02')]
    while (useGameStore.getState().isShowLoading && textRef) {
      setTutorialAnimationIndex(0)
      playTutorial(textArray[0], 'tutorial1')
      await sleep(firstTime ? changeSec + 3000 : changeSec)
      setTutorialAnimationIndex(1)
      firstTime = false
      playTutorial(textArray[1], 'tutorial2')
      await sleep(changeSec)
    }
  }

  const playTutorial = async (displayText: string, animation: string) => {
    if (!textRef.current) return
    const textStyle = textRef.current.children[0] as PixiText
    textStyle.anchor.set(0.5, 0.5)
    textStyle.x = 0
    textStyle.y = 20
    textStyle.text = displayText
    fadeInAnimation(textStyle, loadingSpine)
    const track = loadingSpine.state.setAnimation(0, animation, true)
    await sleep((track.animation?.duration as number) * 2000)
  }

  const updateLoadingBar = async () => {
    await Assets.load(getloadingPageSpineData())
    if (!percentageRef.current || !loadingBarRef.current) return
    await sleep(2000) // wait for godzi logo
    const textObject = new PixiText()
    textObject.style = textStyle2
    percentageRef.current.addChild(textObject)
    const percentageNode = percentageRef.current.getChildAt(0) as BitmapText
    textProcessAnimation(0, 90, 1, percentageNode)

    const spine = Spine.from('loadingPageSkel', 'loadingPageAtlas', { autoUpdate: true })
    const track = spine.state.setAnimation(0, 'loading90', false)
    loadingBarRef.current.addChild(spine)
    await sleep((track.animation?.duration as number) * 1000)

    await sleep(500)
    loadingBarRef.current.removeChildren()
    percentageRef.current.removeChildren()
    setIsLoadingDone(true)
    spine.destroy()

    const text = new PixiText(
      t('freeGame_tap_anywhere'),
      new TextStyle({
        fontSize: 50,
        fill: '#FFC0D8',
        align: 'center',
      }),
    )
    text.anchor.set(0.5)
    text.x = 0
    text.y = 0

    loadingBarRef.current.addChild(text)
    freeGameService.tapAnywhereAnimation(text)
    const loadingTime = startLoadingTime - window.performance.timing.navigationStart
    ga4Info.addEvent(Ga4EventType.tutorial_loading_time, { sec: `${loadingTime}` })

    if (!loadingContainerRef.current) return
    loadingContainerRef.current.parent.onpointertap = () => {
      if (!loadingBarRef.current) return

      useGameStore.setState({ isShowLoading: false })
      loadingBarRef.current.removeChildren()
      loadingSpine.destroy()
      ga4Info.addEvent(Ga4EventType.enter_main_game)
      // godziDashboardService.init()
      // godziDashboardService.autoShowDashboard()
    }
  }

  const fadeInAnimation = (text: PixiText, spine: Spine) => {
    const tl = gsap.timeline({
      repeat: 0,
      yoyo: false,
      onComplete: () => {
        tl.kill()
      },
    })

    tl.fromTo(
      [text, spine],
      { alpha: 0 },
      {
        duration: 1,
        alpha: 1,
        ease: 'sine.inOut',
      },
    )
  }

  const textProcessAnimation = (
    startNum: number,
    endNum: number,
    duration: number,
    node: BitmapText | Text,
  ): [Promise<void>, () => void] => {
    const displayNum = { value: startNum }
    let isSkip = false
    const numberNode = node as BitmapText
    const anim = new Promise<void>((resolve) => {
      const task = gsap.to(displayNum, {
        value: endNum,
        duration: duration,
        onUpdate: () => {
          if (isSkip) {
            numberNode.text = `${endNum}%`
            resolve()
            task.kill()
            return
          }
          numberNode.text = `${Math.floor(displayNum.value)}%`
        },
        onComplete: () => {
          resolve()
        },
      })
    })
    return [
      anim,
      () => {
        isSkip = true
      },
    ]
  }

  const onPointer = () => {
    if (!isLoadingDone) return
    ga4Info.recordStayTime()
  }

  return (
    <Container
      name="LoadingPage"
      eventMode="static"
      cursor={cursorSetting}
      ref={(el: PixiContainer | null) => {
        loadingContainerRef.current = el as PixiContainer
      }}
      onpointerdown={onPointer}
    >
      <Image
        name="LoadingBackground"
        image="loadingBg"
        width={1080}
        height={2920}
        anchor={[0.5, 0.5]}
        x={gameConfig.resolution.width / 2}
        y={gameConfig.resolution.height / 2}
      />

      {enableTutorialLogo && (
        <Image
          name="LoadingLogo"
          image="loadingLogo"
          width={574}
          height={341}
          anchor={[0.5, 0.5]}
          x={gameConfig.resolution.width / 2}
          y={200}
        />
      )}

      <Container
        name="tutorialAnimation"
        x={gameConfig.resolution.width / 2}
        y={gameConfig.resolution.height / 2 - 50}
        ref={(el: PixiContainer | null) => {
          tutorialRef.current = el as PixiContainer
        }}
      />

      <FixedContainerV2
        name="tutorialText"
        x={0}
        y={gameConfig.resolution.height / 2 + 380}
        anchor={[0.5, 0.5]}
        width={gameConfig.resolution.width}
        height={200}
      >
        <Container
          name="description"
          x={gameConfig.resolution.width / 2}
          y={20}
          width={gameConfig.resolution.width}
          ref={(el: PixiContainer | null) => {
            textRef.current = el as PixiContainer
          }}
        />
        <Container x={gameConfig.resolution.width / 2 - 145} y={150} anchor={0.5}>
          {Array.from({ length: 2 }).map((_, index) => {
            return (
              <Image
                name={`dot${index + 1}`}
                image={tutorialAnimationIndex === index ? 'enabledDot' : 'disabledDot'}
                width={20}
                height={20}
                x={80 + index * 60}
                y={0}
                key={`dot${index + 1}`}
              />
            )
          })}
        </Container>
      </FixedContainerV2>

      <Container
        name="loadingBar"
        x={gameConfig.resolution.width / 2}
        y={gameConfig.resolution.height - 300}
        anchor={[0.5, 0.5]}
        ref={(el: PixiContainer | null) => {
          loadingBarRef.current = el as PixiContainer
        }}
      />

      <Container
        ref={(el: PixiContainer | null) => {
          percentageRef.current = el as PixiContainer
        }}
        name="percentageRef"
        x={gameConfig.resolution.width / 2 - 50}
        y={gameConfig.resolution.height - 200}
        anchor={[0.5, 0.5]}
      />
    </Container>
  )
}
